import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  Grid,
  TextField,
  Autocomplete
} from '@mui/material';
import {
  LocalShipping as LocalShippingIcon,
  Add as AddIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { useUser } from '../contexts/UserContext';
import FiltrosCierresDiarios from './FiltrosCierresDiarios';
import TablaCierresDiarios from './TablaCierresDiarios';
import ModalDetalleComparacion from './ModalDetalleComparacion';
import ModalEditarTransacciones from './ModalEditarTransacciones';
import TablaTransporteValores from './TablaTransporteValores';
import ModalNuevoPTV from './ModalNuevoPTV';

const ControlCierresDiarios = () => {
  const server = process.env.REACT_APP_API_ROUTE;
  const { user } = useUser();

  // Tab activo: 0 = Cierres, 1 = Transporte de Valores
  const [tabActivo, setTabActivo] = useState(0);

  // Filtros compartidos entre pestañas
  const [filtrosActuales, setFiltrosActuales] = useState(null);

  const [loading, setLoading] = useState(false);
  const [datos, setDatos] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [fechaInicioConsulta, setFechaInicioConsulta] = useState(null);
  const [fechaFinConsulta, setFechaFinConsulta] = useState(null);
  const [error, setError] = useState(null);

  // Estado para el modal de detalle
  const [modalOpen, setModalOpen] = useState(false);
  const [cierreSeleccionado, setCierreSeleccionado] = useState(null);
  const [detalleComparacion, setDetalleComparacion] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  // Estados para edición de transacciones
  const [modalEditarTransacciones, setModalEditarTransacciones] = useState(false);
  const [transaccionesCierre, setTransaccionesCierre] = useState([]);
  const [loadingTransacciones, setLoadingTransacciones] = useState(false);

  // Estados para Transporte de Valores
  const [datosTV, setDatosTV] = useState([]);
  const [cuentasBancariasTV, setCuentasBancariasTV] = useState([]);
  const [loadingTV, setLoadingTV] = useState(false);
  const [modalNuevoPTV, setModalNuevoPTV] = useState(false);
  const [idNuevoPTV, setIdNuevoPTV] = useState(null);
  const [filtroCuentaTV, setFiltroCuentaTV] = useState(null);

  const normalizarTexto = (value) => (value || '').toString().trim().toLowerCase();

  const primerValor = (...values) => {
    for (const value of values) {
      if (value !== null && value !== undefined && String(value).trim() !== '') return value;
    }
    return '';
  };

  const esTipoCuenta = (value) => {
    const text = normalizarTexto(value);
    return text === 'ahorro' || text === 'corriente' || text === 'cheques';
  };

  const pareceNumeroCuenta = (value) => {
    const text = (value || '').toString().trim();
    if (!text) return false;
    return /\d/.test(text) || text.includes('_') || text.toLowerCase().includes('cuenta');
  };

  const cargarCuentasBancarias = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${server}/ChkBancosCuentasBancarias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const arr = data.$values || data || [];
        setCuentasBancariasTV(Array.isArray(arr) ? arr : []);
      }
    } catch (err) {
      console.error('Error al cargar cuentas bancarias:', err);
    }
  };

  useEffect(() => {
    cargarCuentasBancarias();
  }, []);

  const enriquecerDatosTV = (datos) => {
    return datos.map((transporte) => {
      const idCuenta = transporte.idCuentaBancaria;
      const cuentaInfo = cuentasBancariasTV.find((c) => c.idCuentaBancaria === idCuenta);
      return {
        ...transporte,
        _cuentaBancariaInfo: cuentaInfo || null
      };
    });
  };

  const datosEnriquecidos = useMemo(() => enriquecerDatosTV(datosTV), [datosTV, cuentasBancariasTV]);

  const obtenerBancoNombreTV = (row) => {
    const navCuenta =
      row?.chkBancosCuentasBancariaNavigation ||
      row?.ChkBancosCuentasBancariaNavigation ||
      row?.cuentaBancariaNavigation ||
      row?.CuentaBancariaNavigation ||
      null;

    const navBanco =
      navCuenta?.chkBancoNavigation ||
      navCuenta?.ChkBancoNavigation ||
      row?.chkBancoNavigation ||
      row?.ChkBancoNavigation ||
      null;

    return String(
      primerValor(
        row?.nombreBanco,
        row?.NombreBanco,
        row?.banco,
        row?.Banco,
        row?.descripcionBanco,
        row?.DescripcionBanco,
        navCuenta?.nombreBanco,
        navCuenta?.NombreBanco,
        navBanco?.nombreBanco,
        navBanco?.NombreBanco
      )
    ).trim();
  };

  const obtenerCuentaInfoTV = (row) => {
    const cuentaInfo = row._cuentaBancariaInfo;
    if (cuentaInfo) {
      const bancoNombre = cuentaInfo?.nombreBanco || cuentaInfo?.NombreBanco || '';
      const tipoCuenta = cuentaInfo?.cuentaBancaria || cuentaInfo?.CuentaBancaria || '';
      const cuentaContable = cuentaInfo?.cuentaContableNew || cuentaInfo?.CuentaContableNew || cuentaInfo?.cuentaContable || cuentaInfo?.CuentaContable || '';

      const numero = tipoCuenta || 'Cuenta';
      const label = bancoNombre ? `${bancoNombre}-${numero}` : numero;
      const key = `cuenta-${cuentaInfo.idCuentaBancaria}`;

      return { key, label, detail: cuentaContable };
    }

    // Fallback a la lógica anterior si no tiene info de cuenta
    const navCuenta =
      row?.chkBancosCuentasBancariaNavigation ||
      row?.ChkBancosCuentasBancariaNavigation ||
      row?.cuentaBancariaNavigation ||
      row?.CuentaBancariaNavigation ||
      null;

    const cuentaNumeroRaw = String(
      primerValor(
        row?.cuentaBancaria,
        row?.CuentaBancaria,
        row?.numeroCuenta,
        row?.NumeroCuenta,
        row?.numeroCuentaBancaria,
        row?.NumeroCuentaBancaria,
        navCuenta?.cuentaBancaria,
        navCuenta?.CuentaBancaria
      )
    ).trim();

    const cuentaId = primerValor(
      row?.idCuentaBancaria,
      row?.IdCuentaBancaria,
      navCuenta?.idCuentaBancaria,
      navCuenta?.IdCuentaBancaria,
      row?.idCuenta,
      row?.IdCuenta
    );

    const cuentaContableRaw = String(
      primerValor(
        row?.cuentaContableNew,
        row?.CuentaContableNew,
        row?.cuentaContable,
        row?.CuentaContable,
        row?.descripcionCuentaContable,
        row?.DescripcionCuentaContable,
        row?.nombreCuentaContable,
        row?.NombreCuentaContable,
        row?.descripcionCuenta,
        row?.DescripcionCuenta,
        row?.nombreCuenta,
        row?.NombreCuenta,
        navCuenta?.cuentaContableNew,
        navCuenta?.CuentaContableNew,
        navCuenta?.cuentaContable,
        navCuenta?.CuentaContable,
        navCuenta?.descripcionCuenta,
        navCuenta?.DescripcionCuenta,
        navCuenta?.nombreCuenta,
        navCuenta?.NombreCuenta
      )
    ).trim();

    let cuentaNumero = cuentaNumeroRaw;
    let cuentaContable = cuentaContableRaw;

    // Algunos payloads traen invertidos los campos (ej: cuentaBancaria=AHORRO y cuentaContableNew=ATL_AHO_...)
    if (esTipoCuenta(cuentaNumeroRaw) && pareceNumeroCuenta(cuentaContableRaw)) {
      cuentaNumero = cuentaContableRaw;
      cuentaContable = cuentaNumeroRaw;
    }

    if (!cuentaNumero && !cuentaId) return null;

    const bancoNombre = obtenerBancoNombreTV(row);
    const key = cuentaId ? `cuenta-id-${cuentaId}` : `cuenta-num-${normalizarTexto(cuentaNumero)}`;
    const numero = cuentaNumero || `Cuenta ${cuentaId}`;
    const label = bancoNombre ? `${bancoNombre}-${numero}` : numero;
    const detail = cuentaContable;

    return { key, label, detail };
  };

  const cuentasTV = useMemo(() => {
    const map = new Map();
    datosEnriquecidos.forEach((row) => {
      const cuentaInfo = obtenerCuentaInfoTV(row);
      if (cuentaInfo) map.set(cuentaInfo.key, cuentaInfo);
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [datosEnriquecidos]);

  const datosTVFiltrados = useMemo(() => {
    return datosEnriquecidos.filter((row) => {
      const cuentaInfo = obtenerCuentaInfoTV(row);

      if (filtroCuentaTV && cuentaInfo?.key !== filtroCuentaTV.key) return false;
      return true;
    });
  }, [datosEnriquecidos, filtroCuentaTV]);

  useEffect(() => {
    if (filtroCuentaTV && !cuentasTV.some((cuenta) => cuenta.key === filtroCuentaTV.key)) {
      setFiltroCuentaTV(null);
    }
  }, [cuentasTV, filtroCuentaTV]);

  // No cargar datos automáticamente, esperar a que el usuario haga clic en Buscar
  // useEffect(() => {
  //   const fechaFin = new Date();
  //   const fechaInicio = new Date();
  //   fechaInicio.setDate(fechaInicio.getDate() - 30);
  //   
  //   buscarCierres({
  //     fechaInicio,
  //     fechaFin
  //   });
  // }, []);

  const buscarCierres = async (filtros) => {
    setFiltrosActuales(filtros);
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      
      if (filtros.fechaInicio) {
        params.append('fechaInicio', filtros.fechaInicio.toISOString());
      }
      if (filtros.fechaFin) {
        params.append('fechaFin', filtros.fechaFin.toISOString());
      }
      if (filtros.idSucursal) {
        params.append('idSucursal', filtros.idSucursal);
      }
      if (filtros.usuario) {
        params.append('usuario', filtros.usuario);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${server}/MgCajaEncabezadoArqueos/ReporteControlCierresDiarios?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        // Capturar el mensaje de error del backend
        const errorMessage = result.message || result.error || 'Error al obtener los datos de cierres diarios';
        throw new Error(errorMessage);
      }
      
      // Manejar respuesta del backend
      if (result.success) {
        // Manejar formato .NET con $values
        let datosArray = [];
        if (Array.isArray(result.datos)) {
          datosArray = result.datos;
        } else if (result.datos && result.datos.$values && Array.isArray(result.datos.$values)) {
          datosArray = result.datos.$values;
        }
        // Extraer el resultado real si cada elemento es un Task con propiedad result
        datosArray = datosArray.map(item => (item && item.result ? item.result : item));
        setDatos(datosArray);
      
        setTotalRegistros(result.totalRegistros || datosArray.length || 0);
        setFechaInicioConsulta(result.fechaInicioConsulta);
        setFechaFinConsulta(result.fechaFinConsulta);
      } else {
        throw new Error(result.message || 'Error al cargar los datos');
      }
    } catch (err) {
      console.error('Error al buscar cierres:', err);
      const errorMsg = err.message || 'Error al cargar los datos';
      setError(errorMsg);
      Swal.fire({
        icon: 'error',
        title: 'Error al cargar cierres',
        text: errorMsg,
        footer: 'Por favor, verifica que el servicio esté funcionando correctamente'
      });
    } finally {
      setLoading(false);
    }
  };

  const verDetalleCierre = async (cierre) => {
    setCierreSeleccionado(cierre);
    setModalOpen(true);
    setLoadingDetalle(true);
    setDetalleComparacion(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${server}/MgCajaEncabezadoArqueos/ComparacionValoresCierre/${cierre.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al obtener el detalle del cierre');
      }

      const result = await response.json();
      // Manejar formato .NET con $values en arrays anidados
        if (result.comparacion && result.comparacion.$values) {
          result.comparacion = result.comparacion.$values;
        }
        if (result.denominaciones && result.denominaciones.detalle && result.denominaciones.detalle.$values) {
          result.denominaciones.detalle = result.denominaciones.detalle.$values;
        }
        
      if (result.success) {
        setDetalleComparacion(result);
      } else {
        throw new Error(result.message || 'Error al cargar el detalle');
      }
    } catch (err) {
      console.error('Error al cargar detalle:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message
      });
    } finally {
      setLoadingDetalle(false);
    }
  };

  const cerrarModal = (recargaRequerida = false) => {
    setModalOpen(false);
    setCierreSeleccionado(null);
    setDetalleComparacion(null);

    if (recargaRequerida && datos.length > 0) {
      recargarConsultaActual();
    }
  };
  
  // Función para abrir el modal de edición de transacciones
  const abrirModalEditarTransacciones = async (cierre) => {
    console.log('🔍 Abriendo modal para cierre:', cierre);
    setCierreSeleccionado(cierre);
    setModalEditarTransacciones(true);
    setLoadingTransacciones(true);
    
    try {
      const token = localStorage.getItem('token');
      const url = `${server}/MgCajaArqueoDetalleSistemas/porIdEncabezado/${cierre.id}`;
      console.log('🌐 Consultando URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        console.error('❌ Error en response:', response.status, response.statusText);
        throw new Error('Error al obtener las transacciones');
      }
      
      const data = await response.json();
      console.log('📦 Datos recibidos:', data);
      
      // Manejar formato .NET con $values
      const transaccionesArray = data.$values || data || [];
      console.log('📋 Transacciones procesadas:', transaccionesArray);
      console.log('📊 Total de transacciones:', transaccionesArray.length);
      
      setTransaccionesCierre(transaccionesArray);
      
      if (transaccionesArray.length === 0) {
        Swal.fire({
          icon: 'info',
          title: 'Sin transacciones',
          text: 'No se encontraron transacciones registradas para este cierre.',
          footer: 'Es posible que el cierre no tenga movimientos registrados'
        });
      }
    } catch (err) {
      console.error('❌ Error al cargar transacciones:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar las transacciones del cierre',
        footer: err.message
      });
    } finally {
      setLoadingTransacciones(false);
    }
  };
  
  const cerrarModalEditarTransacciones = (recargaRequerida = false) => {
    setModalEditarTransacciones(false);
    setTransaccionesCierre([]);
    
    // Si se hicieron cambios, recargar los datos
    if (recargaRequerida && datos.length > 0) {
      recargarConsultaActual();
    }
  };

  const imprimirCierre = async (cierre) => {
    try {
      Swal.fire({
        title: 'Generando documento...',
        text: 'Por favor espere',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${server}/MgCajaEncabezadoArqueos/GenerarDocumentoCierre/${cierre.id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error al generar el documento');
      }

      const result = await response.json();
      
      if (result.success && result.pdfBase64) {
        // Convertir base64 a blob
        const byteCharacters = atob(result.pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        // Crear URL del blob
        const pdfUrl = URL.createObjectURL(blob);
        
        Swal.close();
        
        // Abrir en nueva ventana para vista previa e impresión
        const newWindow = window.open(pdfUrl, '_blank');
        if (newWindow) {
          newWindow.onload = () => {
            // Liberar URL después de cargar
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 100);
          };
        } else {
          // Si no se puede abrir ventana, descargar archivo
          const link = document.createElement('a');
          link.href = pdfUrl;
          link.download = result.fileName || `CierreCaja_${cierre.id}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(pdfUrl);
          
          Swal.fire({
            icon: 'success',
            title: 'Documento descargado',
            text: 'El documento se ha descargado correctamente',
            timer: 2000
          });
        }
      } else {
        throw new Error('No se recibió el PDF del servidor');
      }
    } catch (err) {
      console.error('Error al imprimir cierre:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error al generar documento',
        text: err.message || 'No se pudo generar el documento de cierre'
      });
    }
  };

  function normalizarFechaFiltro(value) {
    if (!value) return null;
    const fecha = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(fecha.getTime())) return null;
    return fecha;
  }

  function construirFiltrosConsultaActual() {
    const fechaInicioFiltro = normalizarFechaFiltro(filtrosActuales?.fechaInicio);
    const fechaFinFiltro = normalizarFechaFiltro(filtrosActuales?.fechaFin);

    return {
      ...(filtrosActuales || {}),
      fechaInicio: fechaInicioFiltro || (fechaInicioConsulta ? new Date(fechaInicioConsulta) : null),
      fechaFin: fechaFinFiltro || (fechaFinConsulta ? new Date(fechaFinConsulta) : null)
    };
  }

  function recargarConsultaActual() {
    const filtros = construirFiltrosConsultaActual();
    buscarCierres(filtros);
  }

  const activarRevisionContable = async (cierre, opciones = {}) => {
    const esMasivo = opciones.esMasivo === true;

    if (esMasivo && Array.isArray(cierre)) {
      const cierres = cierre;
      const result = await Swal.fire({
        icon: 'question',
        title: '¿Activar Revisión Contable Masiva?',
        html: `Se activará revisión contable para <strong>${cierres.length}</strong> cierres seleccionados.<br><br>¿Desea continuar?`,
        showCancelButton: true,
        confirmButtonText: 'Activar seleccionados',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#1565c0'
      });

      if (!result.isConfirmed) return;

      Swal.fire({
        title: 'Activando revisión contable... ',
        text: 'Por favor espere',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });

      try {
        const token = localStorage.getItem('token');
        const resultados = await Promise.allSettled(
          cierres.map((item) =>
            fetch(
              `${server}/MgCajaEncabezadoArqueos/revisioncontable?idCierre=${item.id}&sistema=${encodeURIComponent(item.sistema || 'Web')}`,
              {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            ).then(async (res) => {
              if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || errorData.error || `Error al activar revisión en cierre ${item.id}`);
              }
              return item.id;
            })
          )
        );

        const exitos = resultados.filter((r) => r.status === 'fulfilled').length;
        const fallidos = resultados.length - exitos;

        if (fallidos > 0) {
          Swal.fire({
            icon: 'warning',
            title: 'Proceso completado con advertencias',
            text: `Exitosos: ${exitos} | Fallidos: ${fallidos}`
          });
        } else {
          Swal.fire({
            icon: 'success',
            title: 'Revisión contable activada',
            text: `Se actualizaron ${exitos} cierres`
          });
        }

        recargarConsultaActual();
      } catch (error) {
        console.error('Error al activar revisión contable masiva:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error al Activar Revisión',
          html: error.message || 'No se pudo activar la revisión contable masiva'
        });
      }
      return;
    }

    const result = await Swal.fire({
      icon: 'question',
      title: '¿Activar Revisión Contable?',
      html: `Marcará este cierre como revisado contablemente:<br><br>
             <strong>Colaborador:</strong> ${cierre.colaborador}<br>
             <strong>Sucursal:</strong> ${cierre.sucursal}<br>
             <strong>Fecha:</strong> ${new Date(cierre.fechaCierre).toLocaleDateString('es-HN')}<br><br>
             ¿Desea continuar?`,
      showCancelButton: true,
      confirmButtonText: 'Activar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1565c0'
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: 'Activando revisión contable...',
      text: 'Por favor espere',
      allowOutsideClick: false,
      didOpen: () => { Swal.showLoading(); }
    });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${server}/MgCajaEncabezadoArqueos/revisioncontable?idCierre=${cierre.id}&sistema=${encodeURIComponent(cierre.sistema || 'Web')}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Error al activar la revisión contable');
      }

      Swal.fire({
        icon: 'success',
        title: 'Revisión Contable Activada',
        text: 'El cierre ha sido marcado como revisado contablemente',
        timer: 3000
      });

      recargarConsultaActual();

    } catch (error) {
      console.error('Error al activar revisión contable:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al Activar Revisión',
        html: error.message || 'No se pudo activar la revisión contable',
        footer: 'Solo se pueden revisar cierres en estado iniciado'
      });
    }
  };

  const abrirCierre = async (cierre) => {
    const usuarioSolicita = user?.codigoUsuario || 'SISTEMA               ';

    const result = await Swal.fire({
      icon: 'warning',
      title: '¿Abrir Cierre?',
      html: `Esto reabrirá el cierre cerrado de:<br><br>
             <strong>Colaborador:</strong> ${cierre.colaborador}<br>
             <strong>Sucursal:</strong> ${cierre.sucursal}<br>
             <strong>Fecha:</strong> ${new Date(cierre.fechaCierre).toLocaleDateString('es-HN')}<br><br>
             ¿Desea continuar?`,
      showCancelButton: true,
      confirmButtonText: 'Abrir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d32f2f'
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: 'Abriendo cierre...',
      text: 'Por favor espere',
      allowOutsideClick: false,
      didOpen: () => { Swal.showLoading(); }
    });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${server}/MgCajaEncabezadoArqueos/AbrirCierre/${cierre.id}/${encodeURIComponent(usuarioSolicita)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Error al abrir el cierre');
      }

      await response.json();

      Swal.fire({
        icon: 'success',
        title: 'Cierre Abierto',
        text: 'El cierre se ha reabierto correctamente',
        timer: 3000
      });

      recargarConsultaActual();

    } catch (error) {
      console.error('Error al abrir cierre:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al Abrir Cierre',
        html: error.message || 'No se pudo abrir el cierre',
        footer: 'Verifique los permisos y el estado del cierre'
      });
    }
  };
  const correrPartidaContable = async (cierre, opciones = {}) => {
    const esMasivo = opciones.esMasivo === true;

    if (esMasivo && Array.isArray(cierre)) {
      const cierres = cierre;
      const result = await Swal.fire({
        icon: 'question',
        title: '¿Correr Partida Contable Masiva?',
        html: `Se correrá partida contable para <strong>${cierres.length}</strong> cierres seleccionados.<br><br>¿Desea continuar?`,
        showCancelButton: true,
        confirmButtonText: 'Correr seleccionados',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#2e7d32'
      });

      if (!result.isConfirmed) return;

      Swal.fire({
        title: 'Corriendo partida contable... ',
        text: 'Por favor espere',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });

      try {
        const token = localStorage.getItem('token');
        const resultados = await Promise.allSettled(
          cierres.map((item) =>
            fetch(
              `${server}/PtaAutomaticas/ventas-cierre?idCierre=${item.id}&sistema=${encodeURIComponent(item.sistema || 'Web')}`,
              {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            ).then(async (res) => {
              if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || errorData.error || `Error al correr partida en cierre ${item.id}`);
              }
              return item.id;
            })
          )
        );

        const exitos = resultados.filter((r) => r.status === 'fulfilled').length;
        const fallidos = resultados.length - exitos;

        if (fallidos > 0) {
          Swal.fire({
            icon: 'warning',
            title: 'Proceso completado con advertencias',
            text: `Exitosos: ${exitos} | Fallidos: ${fallidos}`
          });
        } else {
          Swal.fire({
            icon: 'success',
            title: 'Partida contable generada',
            text: `Se actualizaron ${exitos} cierres`
          });
        }

        recargarConsultaActual();
      } catch (error) {
        console.error('Error al correr partida contable masiva:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error al Correr Partida',
          html: error.message || 'No se pudo generar la partida contable masiva'
        });
      }
      return;
    }

    const result = await Swal.fire({
      icon: 'question',
      title: '¿Correr Partida Contable?',
      html: `Se generará la partida contable automática para:<br><br>
             <strong>Colaborador:</strong> ${cierre.colaborador}<br>
             <strong>Sucursal:</strong> ${cierre.sucursal}<br>
             <strong>Fecha:</strong> ${new Date(cierre.fechaCierre).toLocaleDateString('es-HN')}<br><br>
             ¿Desea continuar?`,
      showCancelButton: true,
      confirmButtonText: 'Correr Partida',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2e7d32'
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: 'Corriendo partida contable...',
      text: 'Por favor espere',
      allowOutsideClick: false,
      didOpen: () => { Swal.showLoading(); }
    });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${server}/PtaAutomaticas/ventas-cierre?idCierre=${cierre.id}&sistema=${encodeURIComponent(cierre.sistema || 'Web')}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Error al correr la partida contable');
      }

      Swal.fire({
        icon: 'success',
        title: 'Partida Contable Generada',
        text: 'La partida contable se corrió correctamente',
        timer: 3000
      });

      recargarConsultaActual();

    } catch (error) {
      console.error('Error al correr partida contable:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al Correr Partida',
        html: error.message || 'No se pudo generar la partida contable',
        footer: 'El cierre debe tener revisión contable activada'
      });
    }
  };
  // ─── Transporte de Valores ───────────────────────────────────────────────
  const buscarTransporteValores = async (filtros) => {
    setLoadingTV(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filtros?.idSucursal) params.append('idSucursal', filtros.idSucursal);
      if (filtros?.fechaInicio) params.append('fechaInicio', filtros.fechaInicio.toISOString().split('T')[0]);
      if (filtros?.fechaFin) params.append('fechaFinal', filtros.fechaFin.toISOString().split('T')[0]);

      const response = await fetch(`${server}/TransporteValores?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al obtener transportes de valores');
      const result = await response.json();
      const arr = result.$values || result.datos?.$values || result.datos || result || [];
      setDatosTV(Array.isArray(arr) ? arr : []);
    } catch (err) {
      console.error('Error al cargar transportes de valores:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    } finally {
      setLoadingTV(false);
    }
  };

  const handleBuscarFiltros = (filtros) => {
    setFiltrosActuales(filtros);
    buscarCierres(filtros);
    buscarTransporteValores(filtros);
  };

  const handleCambioTab = (_, nuevoTab) => {
    setTabActivo(nuevoTab);
    // Al cambiar de tab, recargar con los filtros actuales
    if (nuevoTab === 1 && filtrosActuales) {
      buscarTransporteValores(filtrosActuales);
    }
  };

  const handlePTVGuardado = (nuevoPTV) => {
    setModalNuevoPTV(false);
    const nuevoId = nuevoPTV?.id || null;
    setIdNuevoPTV(nuevoId);
    // Recargar lista PTV y enfocar en pestaña de transporte de valores
    buscarTransporteValores(filtrosActuales || {});
    setTabActivo(1);
  };

  const exportarExcelTransporteValores = () => {
    if (!datosTVFiltrados || datosTVFiltrados.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sin datos para exportar',
        text: 'No hay registros en la lista actual de envios'
      });
      return;
    }

    const formatearFecha = (value) => {
      if (!value) return '-';
      const fecha = new Date(value);
      if (Number.isNaN(fecha.getTime()) || fecha.getFullYear() <= 1900) return '-';
      return fecha.toLocaleDateString('es-HN');
    };

    const formatearMonto = (value) => {
      const monto = Number(value || 0);
      return monto.toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const datosExcel = datosTVFiltrados.map((row, index) => {
      const cuentaInfo = obtenerCuentaInfoTV(row);
      return {
        '#': index + 1,
        'Codigo PTV': row.codigoInterno || '-',
        'Codigo Cierre': row.codigoCierre || '-',
        'Trazabilidad': row.trazabilidad || '-',
        'Empresa Servicio': row.empresaServicio || '-',
        'Cuenta Bancaria': cuentaInfo?.label || '-',
        'Cuenta Contable': cuentaInfo?.detail || '-',
        'Fecha Envio': formatearFecha(row.fechaEnvio),
        'Fecha Deposito': formatearFecha(row.fechaDeposito),
        'Fecha Arqueo': formatearFecha(row.fechaArqueo),
        'Total Efectivo': formatearMonto(row.totalEfectivoEnvio),
        'Observacion': row.observacion || '-',
        'Ingresado Por': row.ingresadoPor || '-',
        'Partida Envio': row.idPartidaEnvio || '-',
        'Partida Deposito': row.idPartidaDeposito || '-'
      };
    });

    const totalEfectivoFiltrado = datosTVFiltrados.reduce(
      (acc, row) => acc + Number(row?.totalEfectivoEnvio || 0),
      0
    );

    // Fila resumen al final, calculada sobre los filtros aplicados en pantalla.
    datosExcel.push({
      '#': '',
      'Codigo PTV': '',
      'Codigo Cierre': '',
      'Trazabilidad': '',
      'Empresa Servicio': '',
      'Cuenta Bancaria': '',
      'Cuenta Contable': '',
      'Fecha Envio': '',
      'Fecha Deposito': '',
      'Fecha Arqueo': '',
      'Total Efectivo': ''
    });

    datosExcel.push({
      '#': `Registros: ${datosTVFiltrados.length}`,
      'Codigo PTV': 'TOTALES',
      'Codigo Cierre': '',
      'Trazabilidad': '',
      'Empresa Servicio': '',
      'Cuenta Bancaria': '',
      'Cuenta Contable': '',
      'Fecha Envio': '',
      'Fecha Deposito': '',
      'Fecha Arqueo': '',
      'Total Efectivo': formatearMonto(totalEfectivoFiltrado),
      'Observacion': '',
      'Ingresado Por': '',
      'Partida Envio': '',
      'Partida Deposito': ''
    });

    const ws = XLSX.utils.json_to_sheet(datosExcel);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'TransporteValores');
    const fechaArchivo = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Transporte_Valores_${fechaArchivo}.xlsx`);
  };

  return (
    <div className="right_col" role="main" style={{ minHeight: '100vh' }}>
      <div className="">
        <div className="page-title">
          <div className="title_left">
            <h3>📊 Control de Cierres Diarios</h3>
          </div>
        </div>
        <div className="clearfix"></div>

        <div className="row">
          <div className="col-md-12 col-sm-12">
            <Paper sx={{ p: 3 }}>
              {/* Filtros compartidos */}
              <FiltrosCierresDiarios onBuscar={handleBuscarFiltros} loading={loading || loadingTV} />

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                <Tabs value={tabActivo} onChange={handleCambioTab}>
                  <Tab label="Cierres Diarios" />
                  <Tab label="Transporte de Valores" icon={<LocalShippingIcon />} iconPosition="start" />
                </Tabs>
              </Box>

              {/* ─── Pestaña Cierres ─── */}
              {tabActivo === 0 && (
                <Box sx={{ mt: 2 }}>
                  {totalRegistros > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Alert severity="info">
                        <strong>Período consultado:</strong>{' '}
                        {fechaInicioConsulta && new Date(fechaInicioConsulta).toLocaleDateString()} -{' '}
                        {fechaFinConsulta && new Date(fechaFinConsulta).toLocaleDateString()}
                        {' | '}
                        <strong>Total de cierres encontrados:</strong> {totalRegistros}
                      </Alert>
                    </Box>
                  )}

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                  )}

                  {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  )}

                  {!loading && datos.length > 0 && (
                    <TablaCierresDiarios
                      datos={datos}
                      onVerDetalle={verDetalleCierre}
                      onImprimir={imprimirCierre}
                      onEditarTransacciones={abrirModalEditarTransacciones}
                      onReejecutarPartida={activarRevisionContable}
                      onCorrerPartida={correrPartidaContable}
                      onAbrirCierre={abrirCierre}
                      server={server}
                    />
                  )}

                  {!loading && datos.length === 0 && !error && totalRegistros === 0 && (
                    <Box sx={{ textAlign: 'center', my: 6, py: 4 }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        👆 Selecciona los filtros y haz clic en "Buscar"
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Utiliza los filtros superiores para consultar los cierres de caja
                      </Typography>
                    </Box>
                  )}

                  {!loading && datos.length === 0 && !error && totalRegistros === null && (
                    <Box sx={{ textAlign: 'center', my: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No se encontraron cierres para el período seleccionado
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* ─── Pestaña Transporte de Valores ─── */}
              {tabActivo === 1 && (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={5}>
                      <Autocomplete
                        options={cuentasTV}
                        getOptionLabel={(option) => option?.label || ''}
                        isOptionEqualToValue={(option, value) => option?.key === value?.key}
                        value={filtroCuentaTV}
                        onChange={(_, value) => setFiltroCuentaTV(value)}
                        noOptionsText="No hay cuentas en los datos cargados"
                        renderOption={(props, option) => (
                          <li {...props} key={option.key}>
                            <Box>
                              <Typography variant="body2">{option.label}</Typography>
                              {option.detail && (
                                <Typography variant="caption" color="text.secondary">{option.detail}</Typography>
                              )}
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} label="Cuenta Bancaria" placeholder="Filtrar por cuenta" fullWidth />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<FileDownloadIcon />}
                      onClick={exportarExcelTransporteValores}
                    >
                      Exportar a Excel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => setModalNuevoPTV(true)}
                    >
                      Nuevo Envío PTV
                    </Button>
                  </Box>

                  {loadingTV && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  )}

                  {!loadingTV && (
                    <TablaTransporteValores
                      datos={datosTVFiltrados}
                      idNuevo={idNuevoPTV}
                      server={server}
                      user={user}
                      onRecarga={() => buscarTransporteValores(filtrosActuales || {})}
                    />
                  )}
                </Box>
              )}
            </Paper>
          </div>
        </div>
      </div>

      {/* Modal de detalle */}
      {modalOpen && (
        <ModalDetalleComparacion
          open={modalOpen}
          onClose={cerrarModal}
          cierre={cierreSeleccionado}
          detalleComparacion={detalleComparacion}
          loading={loadingDetalle}
          onRecargarDetalle={verDetalleCierre}
        />
      )}

      {/* Modal de edición de transacciones */}
      {modalEditarTransacciones && (
        <ModalEditarTransacciones
          open={modalEditarTransacciones}
          onClose={cerrarModalEditarTransacciones}
          cierre={cierreSeleccionado}
          transacciones={transaccionesCierre}
          loading={loadingTransacciones}
          server={server}
        />
      )}

      {/* Modal Nuevo PTV */}
      {modalNuevoPTV && (
        <ModalNuevoPTV
          open={modalNuevoPTV}
          onClose={() => setModalNuevoPTV(false)}
          onGuardado={handlePTVGuardado}
          server={server}
          user={user}
          filtros={filtrosActuales}
        />
      )}
    </div>
  );
};

export default ControlCierresDiarios;
